import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import GovtJobCropPanel from './GovtJobCropPanel';
import GovtJobPhotoSignPreview from './GovtJobPhotoSignPreview';
import {
    PHOTO_SPEC,
    SIGNATURE_SPEC,
    cropToSpec,
    formatFileSize,
    isSupportedGovtJobFile,
    renderPhotoPreview,
    renderSignaturePreview,
} from './govtJobPhotoSignEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

export default function GovtJobPhotoSignImage() {
    const { t } = useTranslation();
    const photoInputRef = useRef(null);
    const signatureInputRef = useRef(null);

    const [photoFile, setPhotoFile] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [photoState, setPhotoState] = useState(null);
    const [signatureState, setSignatureState] = useState(null);
    const [draggingSlot, setDraggingSlot] = useState(null);
    const [livePhoto, setLivePhoto] = useState(null);
    const [liveSignature, setLiveSignature] = useState(null);
    const [results, setResults] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const pickFile = useCallback((incoming) => {
        const file = Array.from(incoming || [])[0];
        if (!file) return null;
        if (!isSupportedGovtJobFile(file)) {
            notifications.show({ title: t('common.error'), message: t('govtJobPhotoSign.errors.invalidType'), color: 'red' });
            return null;
        }
        if (file.size > MAX_FILE_BYTES) {
            notifications.show({ title: t('common.error'), message: t('govtJobPhotoSign.errors.tooLarge'), color: 'red' });
            return null;
        }
        return file;
    }, [t]);

    const assignFile = useCallback((slot, file) => {
        if (!file) return;
        if (slot === 'photo') {
            setPhotoFile(file);
            setPhotoState(null);
        } else {
            setSignatureFile(file);
            setSignatureState(null);
        }
        setResults(null);
        setError(null);
    }, []);

    const handleDrop = (slot, e) => {
        e.preventDefault();
        setDraggingSlot(null);
        assignFile(slot, pickFile(e.dataTransfer.files));
    };

    const handleReset = () => {
        setPhotoFile(null);
        setSignatureFile(null);
        setPhotoState(null);
        setSignatureState(null);
        setLivePhoto(null);
        setLiveSignature(null);
        setResults(null);
        setError(null);
    };

    const handleExport = async () => {
        if (!photoState?.image || !photoState?.crop || !signatureState?.image || !signatureState?.crop || processing) {
            return;
        }

        recordToolUsage('image/govt-job-photo-sign', { category: 'image' });

        setProcessing(true);
        setResults(null);
        setError(null);

        try {
            const [photoBlob, signatureBlob] = await Promise.all([
                cropToSpec(photoState.image, photoState.crop, PHOTO_SPEC),
                cropToSpec(signatureState.image, signatureState.crop, SIGNATURE_SPEC),
            ]);

            setResults({ photoBlob, signatureBlob });
            setLivePhoto(photoBlob);
            setLiveSignature(signatureBlob);
            notifications.show({ title: t('common.success'), message: t('govtJobPhotoSign.done'), color: 'green' });
        } catch {
            const msg = t('govtJobPhotoSign.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const timer = setTimeout(async () => {
            try {
                if (photoState?.image && photoState.crop) {
                    const blob = await renderPhotoPreview(photoState.image, photoState.crop);
                    if (!cancelled) setLivePhoto(blob);
                } else if (!cancelled) {
                    setLivePhoto(null);
                }

                if (signatureState?.image && signatureState.crop) {
                    const blob = await renderSignaturePreview(signatureState.image, signatureState.crop);
                    if (!cancelled) setLiveSignature(blob);
                } else if (!cancelled) {
                    setLiveSignature(null);
                }
            } catch {
                if (!cancelled) {
                    setLivePhoto(null);
                    setLiveSignature(null);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [photoState, signatureState]);

    const canRun = Boolean(photoState?.crop && signatureState?.crop);
    const hasInput = Boolean(photoFile || signatureFile);
    const previewPhoto = results?.photoBlob ?? livePhoto;
    const previewSignature = results?.signatureBlob ?? liveSignature;

    const status = deriveToolStatus({
        hasInput,
        canRun,
        processing,
        hasResult: Boolean(results),
        hasError: Boolean(error),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`govtJobPhotoSign.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('govtJobPhotoSign.readyHint'),
        success: t('govtJobPhotoSign.done'),
    });

    const resultDownloads = useMemo(() => {
        if (!results) return undefined;
        return [
            {
                id: 'photo',
                name: 'photo.jpg',
                meta: formatFileSize(results.photoBlob.size),
                onDownload: () => downloadBlob(results.photoBlob, 'photo.jpg'),
            },
            {
                id: 'signature',
                name: 'signature.jpg',
                meta: formatFileSize(results.signatureBlob.size),
                onDownload: () => downloadBlob(results.signatureBlob, 'signature.jpg'),
            },
        ];
    }, [results]);

    const handleDownloadZip = async () => {
        if (!results) return;
        const zip = await zipImageFiles([
            { name: 'photo.jpg', blob: results.photoBlob },
            { name: 'signature.jpg', blob: results.signatureBlob },
        ]);
        downloadBlob(zip, 'photo-signature.zip');
    };

    const sizeNote = results ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {t('govtJobPhotoSign.photoSize')}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                {formatFileSize(results.photoBlob.size)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">·</span>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {t('govtJobPhotoSign.signatureSize')}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                {formatFileSize(results.signatureBlob.size)}
            </span>
        </>
    ) : null;

    return (
        <ToolPageLayout
            slug="image/govt-job-photo-sign"
            subtitleKey="govtJobPhotoSign.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('govtJobPhotoSign.cropTitle')}
            howToSteps={howToSteps}
            onReset={hasInput || results || error ? handleReset : undefined}
            primaryAction={{
                label: t('govtJobPhotoSign.export'),
                onClick: handleExport,
                disabled: !canRun || processing || Boolean(results),
                loading: processing,
            }}
            controls={(
                <div className="space-y-5">
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { assignFile('photo', pickFile(e.target.files)); e.target.value = ''; }}
                    />
                    <input
                        ref={signatureInputRef}
                        type="file"
                        accept={ACCEPT}
                        className="hidden"
                        onChange={(e) => { assignFile('signature', pickFile(e.target.files)); e.target.value = ''; }}
                    />

                    <GovtJobCropPanel
                        kind="photo"
                        file={photoFile}
                        isDragging={draggingSlot === 'photo'}
                        onPick={() => photoInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDraggingSlot('photo'); }}
                        onDragLeave={() => setDraggingSlot(null)}
                        onDrop={(e) => handleDrop('photo', e)}
                        onRemove={() => { setPhotoFile(null); setPhotoState(null); setResults(null); }}
                        onCropStateChange={setPhotoState}
                    />

                    <GovtJobCropPanel
                        kind="signature"
                        file={signatureFile}
                        isDragging={draggingSlot === 'signature'}
                        onPick={() => signatureInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDraggingSlot('signature'); }}
                        onDragLeave={() => setDraggingSlot(null)}
                        onDrop={(e) => handleDrop('signature', e)}
                        onRemove={() => { setSignatureFile(null); setSignatureState(null); setResults(null); }}
                        onCropStateChange={setSignatureState}
                    />
                </div>
            )}
            settings={(
                <ToolCard title={t('govtJobPhotoSign.specsTitle')}>
                    <ul className="space-y-2 text-[12.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        <li>{t('govtJobPhotoSign.specPhoto')}</li>
                        <li>{t('govtJobPhotoSign.specSignature')}</li>
                        <li>{t('govtJobPhotoSign.specFormat')}</li>
                    </ul>
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('govtJobPhotoSign.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('govtJobPhotoSign.previewEmpty')}
                    idleHint={t('govtJobPhotoSign.previewEmptyHint')}
                    processingLabel={t('govtJobPhotoSign.exporting')}
                    error={error}
                    successLabel={t('govtJobPhotoSign.done')}
                    sizeNote={sizeNote}
                    downloads={resultDownloads}
                    bundleDownload={results
                        ? { label: t('govtJobPhotoSign.downloadZip'), onClick: handleDownloadZip }
                        : undefined}
                    onAgain={results ? handleReset : undefined}
                    againLabel={t('govtJobPhotoSign.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('govtJobPhotoSign.export')} /> : null}
                    showIdle={!hasInput && !error}
                >
                    {(previewPhoto || previewSignature) && !processing && !error ? (
                        <GovtJobPhotoSignPreview
                            photoBlob={previewPhoto}
                            signatureBlob={previewSignature}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
